import { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Brain,
  ChevronDown,
  ChevronRight,
  Loader2,
  Sparkles,
} from "lucide-react";
import ArtifactQualityReview from "../../ArtifactQualityReview";
import CreditCostTag from "../../CreditCostTag";
import { desiredStateHasQuantifiedTarget } from "./problemStatement";
import {
  hasOperationalDefinitionContent,
  parseOperationalDefinitionNotes,
  serializeOperationalDefinitionNotes,
} from "./operationalDefinition";
import { DefineAiDisclosure } from "./DefineAiDisclosure";
import {
  AssistanceToolCard,
  deriveSimpleFieldStatus,
  StickyFormActionBar,
  useFieldAnchors,
  WorkflowField,
  WorkflowStepProgressSummary,
  WorkflowStepSection,
} from "./WorkflowPatterns";

const PROBLEM_FIELD_TARGETS = {
  "Financial Linkage": "impact",
  "Scope Clarity": "scope",
  "Measurability": "current_state",
  "Operational Definition": "operational_definition_metric_name",
  Completeness: "problem_text",
};

function ToolStateNotice({ tone = "info", title, message, testId }) {
  const toneClassName = tone === "error"
    ? "border-red-500/25 bg-red-500/10 text-red-100"
    : tone === "warning"
      ? "border-amber-500/25 bg-amber-500/10 text-amber-100"
      : "border-border/70 bg-background/30 text-muted-foreground";
  const iconClassName = tone === "error" ? "text-red-300" : tone === "warning" ? "text-amber-300" : "text-primary";

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${toneClassName}`} data-testid={testId}>
      <div className="flex items-start gap-2">
        <AlertCircle className={`mt-0.5 h-4 w-4 shrink-0 ${iconClassName}`} />
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em]">{title}</p>
          <p className="text-xs leading-5">{message}</p>
        </div>
      </div>
    </div>
  );
}

function deriveProblemStepStates(problemStatement, operationalDefinition, desiredStateWarning, problemErrors) {
  const hasProblemText = Boolean(problemStatement.problem_text?.trim());
  const hasCurrentState = Boolean(problemStatement.current_state?.trim());
  const hasDesiredState = Boolean(problemStatement.desired_state?.trim());
  const hasImpact = Boolean(problemStatement.impact?.trim());
  const hasScope = Boolean(problemStatement.scope?.trim());
  const hasOperationalDefinition = hasOperationalDefinitionContent(operationalDefinition);

  const step1Status = hasProblemText
    ? "complete"
    : "not_started";

  const step2Status = hasCurrentState && hasDesiredState && !desiredStateWarning
    ? "complete"
    : hasCurrentState || hasDesiredState || Boolean(desiredStateWarning)
      ? "needs_work"
      : "not_started";

  const step3Status = hasImpact && hasScope && !problemErrors?.impact
    ? "complete"
    : hasImpact || hasScope || Boolean(problemErrors?.impact)
      ? "needs_work"
      : "not_started";

  return [
    { number: 1, title: "Frame the Problem", status: step1Status },
    { number: 2, title: "Describe the Current State and Objective", status: step2Status },
    { number: 3, title: "Define the Business Case and Boundaries", status: step3Status },
    { number: 4, title: "Draft the Operational Definition", status: hasOperationalDefinition ? "complete" : "not_started" },
  ];
}

function deriveProblemGuidance({ problemStatement, desiredStateWarning, problemErrors, stepStates }) {
  if (stepStates[0]?.status !== "complete") {
    return {
      focus: "Start with the problem frame",
      summary: "Write the current issue in plain business language before refining details.",
      tips: [
        "Describe what is happening now, where it appears, and why it matters.",
        "Keep the statement concise and avoid claiming validated baseline numbers unless Measure already confirmed them.",
      ],
    };
  }

  if (stepStates[1]?.status !== "complete") {
    return {
      focus: "Clarify the current state and objective",
      summary: "Define what the team is seeing now and the intended future condition.",
      tips: [
        "Use Current Condition for symptoms or directional evidence that will later be validated in Measure.",
        desiredStateWarning
          ? "Your objective reads like a provisional target. Keep it clearly tied to post-baseline validation."
          : "Keep the Project Objective outcome-oriented instead of overcommitting to a fully validated target.",
      ],
    };
  }

  if (stepStates[2]?.status !== "complete") {
    return {
      focus: "Tighten business case and boundaries",
      summary: "Complete the problem framing by showing why the issue matters and where the work starts and stops.",
      tips: [
        problemErrors?.impact
          ? "Business Impact still needs a clear consequence in business terms such as cost, delay, risk, or customer effect."
          : "State the business consequence in one concise sentence, with rough numbers only if you already have them.",
        "Define the scope boundary so the project stays disciplined and charter-ready.",
      ],
    };
  }

  if (problemStatement.problem_text?.trim() && problemStatement.impact?.trim() && problemStatement.scope?.trim()) {
    return {
      focus: "Problem framing is in good shape",
      summary: "The core Define inputs are in place. Use this pass to tighten wording and remove ambiguity before moving to SMART Goal.",
      tips: [
        "Check that the problem statement, impact, and scope all describe the same operational issue.",
        "Refine wording only where it improves clarity or decision readiness.",
      ],
    };
  }

  return {
    focus: "Use guidance as a quick check",
    summary: "Keep Define focused on clarity, business consequence, and disciplined scope.",
    tips: [
      "Write the problem first, then fill in current condition, objective, impact, and scope.",
      "Avoid adding more detail than the team can currently validate.",
    ],
  };
}

export function ProblemTab({
  projectId,
  token,
  problemStatement,
  setProblemStatement,
  operationalDefinition,
  setOperationalDefinition,
  saving,
  refining,
  aiGenerating,
  problemDirty,
  aiCtqs,
  aiScope,
  aiCharter,
  aiProblemMissingInfo,
  aiProblemFollowUps,
  aiProblemFollowUpAnswers,
  aiProblemAssumptions,
  aiProblemUpdating,
  problemAiAssisted,
  operationalDefinitionAiStatus,
  confirmOperationalDefinitionOverwriteOpen,
  desiredStateWarning,
  problemErrors,
  savedNotice,
  saveProblemStatement,
  refineProblemWithAI,
  requestOperationalDefinitionGeneration,
  setConfirmOperationalDefinitionOverwriteOpen,
  confirmOperationalDefinitionOverwrite,
  updateAiProblemFollowUpAnswer,
  applyProblemFollowUpAnswers,
  generateCtqs,
  generateScope,
  generateCharter,
  aiAssistStatus,
  continueToSmartGoal,
}) {
  const [desiredStateMode, setDesiredStateMode] = useState(
    desiredStateHasQuantifiedTarget(problemStatement.desired_state) ? "target" : "intent",
  );
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [assistanceOpen, setAssistanceOpen] = useState(false);
  const { setFieldContainerRef, setFieldInputRef, focusField } = useFieldAnchors();

  useEffect(() => {
    if (desiredStateHasQuantifiedTarget(problemStatement.desired_state)) {
      setDesiredStateMode("target");
      return;
    }
    if (!problemStatement.desired_state?.trim()) {
      setDesiredStateMode("intent");
    }
  }, [problemStatement.desired_state]);

  const handleImprovementAction = (item) => {
    const fieldKey = PROBLEM_FIELD_TARGETS[item?.criterion] || "problem_text";
    focusField(fieldKey);
  };

  const assistanceResultCount = [aiCtqs, aiScope, aiCharter].filter(
    (item) => item && !item.ai_unavailable,
  ).length;
  const stepStates = deriveProblemStepStates(problemStatement, operationalDefinition, desiredStateWarning, problemErrors);
  const fieldStatuses = {
    problem_text: deriveSimpleFieldStatus({
      hasValue: Boolean(problemStatement.problem_text?.trim()),
      isComplete: Boolean(problemStatement.problem_text?.trim()),
    }),
    current_state: deriveSimpleFieldStatus({
      hasValue: Boolean(problemStatement.current_state?.trim()),
      isComplete: Boolean(problemStatement.current_state?.trim()),
    }),
    desired_state: deriveSimpleFieldStatus({
      hasValue: Boolean(problemStatement.desired_state?.trim()),
      isComplete: Boolean(problemStatement.desired_state?.trim()) && !desiredStateWarning,
      needsWork: Boolean(desiredStateWarning),
    }),
    impact: deriveSimpleFieldStatus({
      hasValue: Boolean(problemStatement.impact?.trim()),
      isComplete: Boolean(problemStatement.impact?.trim()) && !problemErrors?.impact,
      needsWork: Boolean(problemErrors?.impact),
    }),
    scope: deriveSimpleFieldStatus({
      hasValue: Boolean(problemStatement.scope?.trim()),
      isComplete: Boolean(problemStatement.scope?.trim()),
    }),
    operational_definition_metric_name: deriveSimpleFieldStatus({
      hasValue: Boolean(operationalDefinition?.metric_name?.trim()),
      isComplete: Boolean(operationalDefinition?.metric_name?.trim()),
    }),
    operational_definition_issue_definition: deriveSimpleFieldStatus({
      hasValue: Boolean(operationalDefinition?.issue_definition?.trim()),
      isComplete: Boolean(operationalDefinition?.issue_definition?.trim()),
    }),
    operational_definition_counting_rule: deriveSimpleFieldStatus({
      hasValue: Boolean(operationalDefinition?.counting_rule?.trim()),
      isComplete: Boolean(operationalDefinition?.counting_rule?.trim()),
    }),
    operational_definition_measurement_basis: deriveSimpleFieldStatus({
      hasValue: Boolean(operationalDefinition?.measurement_basis?.trim()),
      isComplete: Boolean(operationalDefinition?.measurement_basis?.trim()),
    }),
    operational_definition_validation_notes: deriveSimpleFieldStatus({
      hasValue: Array.isArray(operationalDefinition?.validation_notes) && operationalDefinition.validation_notes.length > 0,
      isComplete: Array.isArray(operationalDefinition?.validation_notes) && operationalDefinition.validation_notes.length > 0,
    }),
  };
  const contextualGuidance = deriveProblemGuidance({
    problemStatement,
    desiredStateWarning,
    problemErrors,
    stepStates,
  });
  const prioritizedFollowUps = Array.isArray(aiProblemFollowUps) ? aiProblemFollowUps : [];
  const missingInformation = Array.isArray(aiProblemMissingInfo) ? aiProblemMissingInfo : [];
  const assumptionsOrUnvalidatedItems = Array.isArray(aiProblemAssumptions) ? aiProblemAssumptions : [];
  const operationalDefinitionNotesValue = serializeOperationalDefinitionNotes(operationalDefinition?.validation_notes);
  const hasOperationalDefinitionDraft = hasOperationalDefinitionContent(operationalDefinition);

  return (
    <div className="space-y-4">
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.75fr)_minmax(320px,1fr)]">
        <div className="min-w-0">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border/60 pb-4">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-heading text-xl font-semibold leading-none">Problem Statement</h3>
                  {problemDirty && (
                    <span
                      className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-400"
                      data-testid="problem-dirty-badge"
                    >
                      Unsaved Changes
                    </span>
                  )}
                  {savedNotice && (
                    <span
                      className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-300"
                      data-testid="problem-saved-notice"
                    >
                      Saved
                    </span>
                  )}
                </div>
                <p className="max-w-3xl text-sm text-muted-foreground">
                  Frame the problem first, then define the current condition, objective, impact, scope, and operational definition in a sequence that is easy to review.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={saveProblemStatement}
                  disabled={saving}
                  className="min-w-[132px]"
                  data-testid="save-problem-btn"
                >
                  {saving ? "Saving..." : "Save Problem"}
                </Button>
                <Button
                  onClick={continueToSmartGoal}
                  className="min-w-[196px]"
                  data-testid="continue-to-smart-btn"
                >
                  Continue to SMART Goal
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-5 pt-5">
              <WorkflowStepProgressSummary
                title="Problem Progress"
                testId="problem-step-progress-summary"
                items={stepStates.map((step) => ({
                  ...step,
                  key: step.number,
                  testId: `problem-step-summary-${step.number}`,
                }))}
              />

              {problemAiAssisted && (
                <DefineAiDisclosure
                  title="AI-assisted wording"
                  detail="This problem statement text was refined with AI. Review any implied baseline, target, or impact wording before treating it as validated."
                  testId="problem-ai-disclosure"
                />
              )}

              {(prioritizedFollowUps.length > 0 || assumptionsOrUnvalidatedItems.length > 0) && (
                <div className="rounded-xl border border-primary/15 bg-primary/5" data-testid="problem-follow-up-panel">
                  <div className="border-b border-primary/10 px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">
                        Additional Information Needed
                      </div>
                      {prioritizedFollowUps.length > 0 && (
                        <span className="text-[11px] text-muted-foreground">
                          {prioritizedFollowUps.length} follow-up {prioritizedFollowUps.length === 1 ? "item" : "items"}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground/85">
                      The first-pass draft populated what could be supported now. These prompts identify the highest-value information still needed to complete or strengthen the Problem page.
                    </p>
                  </div>

                  <div className="space-y-3 px-4 py-4">
                    {prioritizedFollowUps.length > 0 && (
                      <div className="space-y-2">
                        {prioritizedFollowUps.map((item, index) => (
                          <div key={`${item.field || "field"}-${index}`} className="rounded-lg border border-border/60 bg-background/40 px-3 py-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                                item.priority === "high"
                                  ? "border border-amber-500/25 bg-amber-500/10 text-amber-300"
                                  : "border border-border/70 bg-background/40 text-muted-foreground"
                              }`}>
                                {item.priority === "high" ? "High Priority" : "Needed"}
                              </span>
                              {item.field && (
                                <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                                  {item.field.replaceAll("_", " ")}
                                </span>
                              )}
                            </div>
                            <p className="mt-2 text-sm leading-6 text-foreground/95">{item.question}</p>
                            {missingInformation.find((entry) => entry.field === item.field && entry.category === item.category)?.reason && (
                              <p className="mt-1 text-[11px] leading-5 text-muted-foreground/80">
                                {missingInformation.find((entry) => entry.field === item.field && entry.category === item.category)?.reason}
                              </p>
                            )}
                            <div className="mt-3">
                              <Textarea
                                value={aiProblemFollowUpAnswers?.[`${String(item.field || "").trim()}::${String(item.category || "").trim()}`] || ""}
                                onChange={(event) => updateAiProblemFollowUpAnswer(
                                  `${String(item.field || "").trim()}::${String(item.category || "").trim()}`,
                                  event.target.value,
                                )}
                                rows={2}
                                autoResize
                                maxHeight={180}
                                placeholder="Add the missing detail here. This will update only the affected Problem-page sections."
                                className="min-h-[84px] text-sm leading-6"
                                data-testid={`problem-follow-up-answer-${index}`}
                              />
                            </div>
                          </div>
                        ))}
                        <div className="flex justify-end pt-1">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={applyProblemFollowUpAnswers}
                            disabled={aiProblemUpdating}
                            className="gap-2"
                            data-testid="problem-follow-up-apply-btn"
                          >
                            {aiProblemUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            Update Affected Sections
                            <CreditCostTag cost={1} />
                          </Button>
                        </div>
                      </div>
                    )}

                    {assumptionsOrUnvalidatedItems.length > 0 && (
                      <div className="space-y-2 border-t border-border/60 pt-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Still Unvalidated</p>
                        <div className="space-y-2">
                          {assumptionsOrUnvalidatedItems.map((item, index) => (
                            <div key={`${item}-${index}`} className="flex items-start gap-2 rounded-lg border border-border/60 bg-background/30 px-3 py-2.5">
                              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                              <p className="text-xs leading-5 text-muted-foreground/90">{item}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-border/70 bg-background/25" data-testid="problem-statement-guidance">
                <button
                  type="button"
                  onClick={() => setGuidanceOpen((current) => !current)}
                  className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-background/30"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium">Workflow Coaching</p>
                      <span className="rounded-full border border-border/70 bg-background/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {contextualGuidance.focus}
                      </span>
                    </div>
                    <p className="text-[11px] leading-5 text-muted-foreground/85">
                      {contextualGuidance.summary}
                    </p>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${guidanceOpen ? "rotate-180" : ""}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-200 ease-out ${guidanceOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}`}>
                  <div className="border-t border-border/60 px-4 py-3">
                    <div className="space-y-2">
                      {contextualGuidance.tips.map((tip, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary/70" aria-hidden="true" />
                          <p className="text-[11px] leading-5 text-muted-foreground/85">{tip}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <WorkflowStepSection
                stepNumber={1}
                title="Frame the Problem"
                status={stepStates[0].status}
                description="Start with the issue in plain business language and refine the wording only after the core facts are on the page."
                actions={(
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={refineProblemWithAI}
                    disabled={refining}
                    className="gap-2 text-muted-foreground hover:text-primary"
                    data-testid="refine-ai-btn"
                  >
                    {refining ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Refine with AI
                    <CreditCostTag cost={1} />
                  </Button>
                )}
              >
                <WorkflowField
                  fieldKey="problem_text"
                  label="Problem Description"
                  metadata="Recommended: 2-4 sentences"
                  status={fieldStatuses.problem_text}
                  containerRef={setFieldContainerRef("problem_text")}
                  helperText="Focus on what is happening now and why it matters. Avoid claiming a validated baseline here unless Measure data already confirms it."
                  hintText="Lead with the current issue, where it appears, and the business consequence."
                >
                  <Textarea
                    id="problem-input-problem_text"
                    ref={setFieldInputRef("problem_text")}
                    placeholder="State the current condition, where the problem appears, the measurable symptom you are seeing, and the improvement objective. Example: Customer order changes are being processed inconsistently, creating delays and rework across the order-entry team; the project objective is to stabilize the process and reduce turnaround time."
                    value={problemStatement.problem_text}
                    onChange={(e) => setProblemStatement((prev) => ({ ...prev, problem_text: e.target.value }))}
                    rows={3}
                    autoResize
                    maxHeight={320}
                    className="min-h-[112px] leading-6 font-normal"
                    data-testid="problem-text-input"
                  />
                </WorkflowField>
              </WorkflowStepSection>

              <WorkflowStepSection
                stepNumber={2}
                title="Describe the Current State and Objective"
                status={stepStates[1].status}
                description="Document what the team is seeing now and the intended future condition so the project has a clear starting point."
              >
                <div className="grid gap-4 lg:grid-cols-2">
                  <WorkflowField
                    fieldKey="current_state"
                    label="Current Condition"
                    metadata="Recommended: 2-3 sentences"
                    status={fieldStatuses.current_state}
                    containerRef={setFieldContainerRef("current_state")}
                    helperText="Define can reference known symptoms or directional evidence, but full baseline validation belongs in Measure."
                  >
                    <Textarea
                      id="problem-input-current_state"
                      ref={setFieldInputRef("current_state")}
                      placeholder="Describe the current process condition, where the issue occurs, and any known symptom or rough magnitude. If exact baseline data is not confirmed yet, note that it will be validated in Measure."
                      value={problemStatement.current_state}
                      onChange={(e) => setProblemStatement((prev) => ({ ...prev, current_state: e.target.value }))}
                      rows={3}
                      autoResize
                      maxHeight={280}
                      className="min-h-[104px] leading-6 font-normal"
                      data-testid="current-state-input"
                    />
                  </WorkflowField>

                  <WorkflowField
                    fieldKey="desired_state"
                    label="Project Objective"
                    metadata={desiredStateMode === "target" ? "Recommended: 1-2 target sentences" : "Recommended: 2-3 sentences"}
                    status={fieldStatuses.desired_state}
                    containerRef={setFieldContainerRef("desired_state")}
                    helperText={desiredStateMode === "target"
                      ? "Optional target mode is for an explicitly chosen provisional target. Keep it tied to post-baseline validation rather than presenting it as already proven."
                      : "Recommended in Define: keep this outcome-oriented and set the precise target level after Measure establishes the baseline."}
                  >
                    <div className="flex flex-wrap items-center gap-2 pb-0.5" data-testid="desired-state-mode-toggle">
                      <button
                        type="button"
                        onClick={() => setDesiredStateMode("intent")}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                          desiredStateMode === "intent"
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border bg-background text-muted-foreground"
                        }`}
                        data-testid="desired-state-intent-mode-btn"
                      >
                        Improvement Intent
                      </button>
                      <button
                        type="button"
                        onClick={() => setDesiredStateMode("target")}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                          desiredStateMode === "target"
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border bg-background text-muted-foreground"
                        }`}
                        data-testid="desired-state-target-mode-btn"
                      >
                        Optional Post-Baseline Target
                      </button>
                    </div>
                    <Textarea
                      id="problem-input-desired_state"
                      ref={setFieldInputRef("desired_state")}
                      placeholder={
                        desiredStateMode === "target"
                          ? "Optional: record a provisional target only if you intentionally want one. Example: After Measure confirms the current turnaround baseline, set a target such as reducing validated average turnaround to 24 hours."
                          : "Describe the desired future condition or improvement intent. Example: Standardize intake and approval flow so orders move through the process without avoidable delay or rework, then set the final target after Measure validates the baseline."
                      }
                      value={problemStatement.desired_state}
                      onChange={(e) => setProblemStatement((prev) => ({ ...prev, desired_state: e.target.value }))}
                      rows={3}
                      autoResize
                      maxHeight={280}
                      className="min-h-[104px] leading-6 font-normal"
                      data-testid="desired-state-input"
                    />
                    {desiredStateWarning && (
                      <div
                        className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
                        data-testid="desired-state-warning"
                      >
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                        <p>{desiredStateWarning.message}</p>
                      </div>
                    )}
                  </WorkflowField>
                </div>
              </WorkflowStepSection>

              <WorkflowStepSection
                stepNumber={3}
                title="Define the Business Case and Boundaries"
                status={stepStates[2].status}
                description="Clarify why the issue matters and where the project starts and stops so the team can move into SMART Goal with clean boundaries."
              >
                <div className="grid gap-4 lg:grid-cols-2">
                  <WorkflowField
                    fieldKey="impact"
                    label={<>Business Impact <span className="text-red-400">*</span></>}
                    metadata="Recommended: 1 concise business impact statement"
                    status={fieldStatuses.impact}
                    containerRef={setFieldContainerRef("impact")}
                    helperText="Required in Define. Qualitative text is acceptable; add a rough numeric estimate only if you have one."
                    error={problemErrors?.impact}
                  >
                    <Input
                      id="problem-input-impact"
                      ref={setFieldInputRef("impact")}
                      placeholder="e.g., scrap cost rising, rework cost burden, throughput instability, delivery risk, customer impact, or an optional numeric estimate"
                      value={problemStatement.impact}
                      onChange={(e) => setProblemStatement((prev) => ({ ...prev, impact: e.target.value }))}
                      data-testid="impact-input"
                      aria-invalid={problemErrors?.impact ? "true" : "false"}
                    />
                  </WorkflowField>

                  <WorkflowField
                    fieldKey="scope"
                    label="Scope"
                    metadata="Recommended: 1 scope boundary statement"
                    status={fieldStatuses.scope}
                    containerRef={setFieldContainerRef("scope")}
                    helperText="Define where the problem starts and stops so the statement stays disciplined and project-ready."
                  >
                    <div className="space-y-2.5">
                      <Input
                        id="problem-input-scope"
                        ref={setFieldInputRef("scope")}
                        placeholder="e.g., order-entry team, Plant 2 packaging line, North America returns process"
                        value={problemStatement.scope}
                        onChange={(e) => setProblemStatement((prev) => ({ ...prev, scope: e.target.value }))}
                        data-testid="scope-input"
                      />
                      <div className="flex justify-start">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={generateScope}
                          disabled={!!aiGenerating || !problemStatement.problem_text}
                          className="h-7 gap-2 px-2 text-[11px] text-muted-foreground hover:text-primary"
                          data-testid="ai-suggest-scope-btn"
                        >
                          {aiGenerating === "Scope Guardrails" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                          Suggest scope guardrails
                          <CreditCostTag cost={1} />
                        </Button>
                      </div>
                    </div>
                  </WorkflowField>
                </div>
              </WorkflowStepSection>

              <WorkflowStepSection
                stepNumber={4}
                title="Draft the Operational Definition"
                status={stepStates[3]?.status || (hasOperationalDefinitionDraft ? "complete" : "not_started")}
                description="Review the AI-generated operational definition as a first-pass draft, or write one manually when the AI response is partial. Keep unresolved measurement details explicit."
                actions={(
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={requestOperationalDefinitionGeneration}
                    disabled={aiGenerating === "Operational Definition" || !problemStatement.problem_text}
                    className="gap-2 text-muted-foreground hover:text-primary"
                    data-testid="generate-operational-definition-btn"
                  >
                    {aiGenerating === "Operational Definition" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {hasOperationalDefinitionDraft ? "Re-generate with AI" : "Generate with AI"}
                    <CreditCostTag cost={1} />
                  </Button>
                )}
                noDivider
              >
                <div className="space-y-4 rounded-xl border border-border/70 bg-background/25 p-4" data-testid="problem-operational-definition-panel">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Operational Definition</p>
                      <p className="max-w-3xl text-xs leading-5 text-muted-foreground">
                        This section turns the Problem draft into a measurable Define-phase draft. It should stay useful without implying that defect criteria, counting logic, denominators, or source details are already validated.
                      </p>
                    </div>
                    {hasOperationalDefinitionDraft ? (
                      <span className="rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">
                        Draft Ready
                      </span>
                    ) : (
                      <span className="rounded-full border border-border/70 bg-background/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                        Add Manually or Generate with AI
                      </span>
                    )}
                  </div>

                  {problemAiAssisted && hasOperationalDefinitionDraft && (
                    <DefineAiDisclosure
                      title="AI-generated operational definition draft"
                      detail="Review the metric wording, counting convention, measurement basis, and validation notes before treating them as confirmed process rules."
                      testId="operational-definition-ai-disclosure"
                    />
                  )}

                  {!hasOperationalDefinitionDraft && (
                    <div className="rounded-lg border border-dashed border-border/70 bg-background/35 px-4 py-3" data-testid="operational-definition-empty-state">
                      <p className="text-xs leading-5 text-muted-foreground">
                        No Operational Definition draft is populated yet. Refine the Problem with AI to generate a first pass, or draft the structure manually below.
                      </p>
                    </div>
                  )}

                  {operationalDefinitionAiStatus?.error && (
                    <ToolStateNotice
                      tone="error"
                      title="Operational Definition Draft Failed"
                      message={operationalDefinitionAiStatus.error}
                      testId="operational-definition-ai-error"
                    />
                  )}

                  {!operationalDefinitionAiStatus?.error && operationalDefinitionAiStatus?.lastUpdated && (
                    <ToolStateNotice
                      title="Operational Definition Draft Updated"
                      message="The AI draft was refreshed for this section only. Review it, then click Save Problem to persist it before treating the counting logic or measurement basis as confirmed."
                      testId="operational-definition-ai-success"
                    />
                  )}

                  <div className="grid gap-4 lg:grid-cols-2">
                    <WorkflowField
                      fieldKey="operational_definition_metric_name"
                      label="Metric Name"
                      metadata="Recommended: 1 primary measure"
                      status={fieldStatuses.operational_definition_metric_name}
                      containerRef={setFieldContainerRef("operational_definition_metric_name")}
                      helperText="Name the primary measure the team intends to define and validate."
                    >
                      <Input
                        id="problem-input-operational_definition_metric_name"
                        ref={setFieldInputRef("operational_definition_metric_name")}
                        placeholder="e.g., defect rate, cycle time, complaint rate, late completion rate"
                        value={operationalDefinition?.metric_name || ""}
                        onChange={(e) => setOperationalDefinition((prev) => ({ ...prev, metric_name: e.target.value }))}
                        data-testid="operational-definition-metric-name-input"
                      />
                    </WorkflowField>

                    <WorkflowField
                      fieldKey="operational_definition_measurement_basis"
                      label="Measurement Basis / Unit"
                      metadata="Recommended: 1-2 sentences"
                      status={fieldStatuses.operational_definition_measurement_basis}
                      containerRef={setFieldContainerRef("operational_definition_measurement_basis")}
                      helperText="Describe the unit of analysis, reporting cadence, and measurement basis without pretending the denominator or source is already settled."
                    >
                      <Textarea
                        id="problem-input-operational_definition_measurement_basis"
                        ref={setFieldInputRef("operational_definition_measurement_basis")}
                        placeholder="Describe the unit, cadence, and basis for measurement. Example: Measure at the order level and summarize weekly; denominator and source-system detail to be confirmed in Measure."
                        value={operationalDefinition?.measurement_basis || ""}
                        onChange={(e) => setOperationalDefinition((prev) => ({ ...prev, measurement_basis: e.target.value }))}
                        rows={3}
                        autoResize
                        maxHeight={220}
                        className="min-h-[104px] leading-6 font-normal"
                        data-testid="operational-definition-measurement-basis-input"
                      />
                    </WorkflowField>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <WorkflowField
                      fieldKey="operational_definition_issue_definition"
                      label="Definition of the Defect / Issue"
                      metadata="Recommended: 2-3 sentences"
                      status={fieldStatuses.operational_definition_issue_definition}
                      containerRef={setFieldContainerRef("operational_definition_issue_definition")}
                      helperText="Describe what qualifies as the issue in draft Define-phase language. If exact criteria are not yet known, say what still needs confirmation."
                    >
                      <Textarea
                        id="problem-input-operational_definition_issue_definition"
                        ref={setFieldInputRef("operational_definition_issue_definition")}
                        placeholder="Draft what counts as the issue, while keeping exact criteria explicit if they still need confirmation."
                        value={operationalDefinition?.issue_definition || ""}
                        onChange={(e) => setOperationalDefinition((prev) => ({ ...prev, issue_definition: e.target.value }))}
                        rows={4}
                        autoResize
                        maxHeight={260}
                        className="min-h-[128px] leading-6 font-normal"
                        data-testid="operational-definition-issue-definition-input"
                      />
                    </WorkflowField>

                    <WorkflowField
                      fieldKey="operational_definition_counting_rule"
                      label="How It Should Be Counted"
                      metadata="Recommended: 2-3 sentences"
                      status={fieldStatuses.operational_definition_counting_rule}
                      containerRef={setFieldContainerRef("operational_definition_counting_rule")}
                      helperText="Document the proposed counting convention, and call out anything that still needs to be validated before it becomes the final measure."
                    >
                      <Textarea
                        id="problem-input-operational_definition_counting_rule"
                        ref={setFieldInputRef("operational_definition_counting_rule")}
                        placeholder="Describe the draft counting convention. Example: Count each affected request once per reporting period; repeat-event handling to be validated with the process owner."
                        value={operationalDefinition?.counting_rule || ""}
                        onChange={(e) => setOperationalDefinition((prev) => ({ ...prev, counting_rule: e.target.value }))}
                        rows={4}
                        autoResize
                        maxHeight={260}
                        className="min-h-[128px] leading-6 font-normal"
                        data-testid="operational-definition-counting-rule-input"
                      />
                    </WorkflowField>
                  </div>

                  <WorkflowField
                    fieldKey="operational_definition_validation_notes"
                    label="Validation Notes"
                    metadata="Recommended: 1 note per line"
                    status={fieldStatuses.operational_definition_validation_notes}
                    containerRef={setFieldContainerRef("operational_definition_validation_notes")}
                    helperText="List what still needs confirmation, such as criteria ownership, denominator, source-system detail, or counting conventions."
                    hintText="Use one line per note so the draft is easy to review with the process owner."
                  >
                    <Textarea
                      id="problem-input-operational_definition_validation_notes"
                      ref={setFieldInputRef("operational_definition_validation_notes")}
                      placeholder={"Examples:\nConfirm with the process owner how repeat events should be counted.\nDenominator to be confirmed in Measure.\nCriteria should align to the current standard work or quality definition."}
                      value={operationalDefinitionNotesValue}
                      onChange={(e) => setOperationalDefinition((prev) => ({
                        ...prev,
                        validation_notes: parseOperationalDefinitionNotes(e.target.value),
                      }))}
                      rows={4}
                      autoResize
                      maxHeight={260}
                      className="min-h-[128px] leading-6 font-normal"
                      data-testid="operational-definition-validation-notes-input"
                    />
                  </WorkflowField>
                </div>
              </WorkflowStepSection>
            </div>

            <StickyFormActionBar
              primaryLabel="Continue to SMART Goal"
              onPrimaryClick={continueToSmartGoal}
              secondaryLabel="Save Problem"
              secondaryLoadingLabel="Saving..."
              secondaryDisabled={saving}
              onSecondaryClick={saveProblemStatement}
            />
          </div>
        </div>

        <ArtifactQualityReview
          projectId={projectId}
          artifactType="problem_statement"
          token={token}
          artifactContent={{
            problem_text: problemStatement.problem_text,
            problem_statement: problemStatement.problem_text,
            current_state: problemStatement.current_state,
            current_condition: problemStatement.current_state,
            desired_state: problemStatement.desired_state,
            project_objective: problemStatement.desired_state,
            impact: problemStatement.impact,
            business_impact: problemStatement.impact,
            scope: problemStatement.scope,
            operational_definition: operationalDefinition,
          }}
          variant="panel"
          title="AI Problem Evaluation"
          description="Use the score to identify the next highest-value fixes before moving to SMART Goal."
          onImprovementAction={handleImprovementAction}
          className="xl:sticky xl:top-4"
        />
      </div>

      <Collapsible open={assistanceOpen} onOpenChange={setAssistanceOpen}>
        <div className="rounded-2xl border border-border bg-card/95 shadow-sm">
          <CollapsibleTrigger
            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            data-testid="problem-ai-assistance-toggle"
          >
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-primary" />
                <h3 className="font-heading text-base font-semibold">AI Assistance</h3>
                {assistanceResultCount > 0 && (
                  <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                    {assistanceResultCount} ready
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Optional draft tools for CTQs, scope guardrails, and charter language. Keep these secondary to completing the Problem tab.
              </p>
            </div>
            {assistanceOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </CollapsibleTrigger>

          <CollapsibleContent className="border-t border-border/60 px-5 py-5">
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-border/60 bg-background/20 px-4 py-3">
                <div className="space-y-1">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Secondary Support</p>
                  <p className="max-w-3xl text-sm text-muted-foreground">
                    Use these tools after the core Problem framing is in place. They help extend the Define work, but they are not required to complete the tab.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-3">
              <AssistanceToolCard
                title="AI: CTQ & Stakeholder Requirements"
                description="Generate likely CTQs and stakeholder needs from the problem framing."
                action={(
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={generateCtqs}
                    disabled={!!aiGenerating || !problemStatement.problem_text}
                    className="gap-2"
                    data-testid="ai-suggest-ctqs-btn"
                  >
                    {aiGenerating === "CTQ Suggestions" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Suggest CTQs
                    <CreditCostTag cost={1} />
                  </Button>
                )}
              >
                {aiAssistStatus?.ctqs?.error && (
                  <ToolStateNotice
                    tone="error"
                    title="CTQ Draft Failed"
                    message={aiAssistStatus.ctqs.error}
                    testId="ai-ctqs-error"
                  />
                )}
                {aiCtqs?.ai_unavailable && (
                  <ToolStateNotice
                    tone="warning"
                    title="CTQ Draft Unavailable"
                    message="The AI service did not return a CTQ draft this time. Retry after saving the latest Define context or try again later."
                    testId="ai-ctqs-unavailable"
                  />
                )}
                {aiCtqs && !aiCtqs.ai_unavailable && (
                  <div className="space-y-3" data-testid="ai-ctqs-panel">
                    <DefineAiDisclosure
                      title="AI-generated stakeholder and CTQ draft"
                      detail="CTQs, success metrics, and suggested targets may reflect assumptions from the problem statement. Validate them with stakeholders before treating them as confirmed."
                      testId="ai-ctqs-disclosure"
                    />
                    {aiCtqs.ctqs?.map((ctq, i) => (
                      <div key={i} className="space-y-1 rounded-lg border border-border bg-card/70 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium">{ctq.ctq_metric}</p>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${ctq.priority === "high" ? "bg-red-500/15 text-red-400" : ctq.priority === "medium" ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>
                            {ctq.priority}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">VOC: {ctq.voc}</p>
                        <p className="text-xs text-muted-foreground">Requirement: {ctq.requirement}</p>
                        <p className="text-xs text-muted-foreground">Target: {ctq.target}</p>
                      </div>
                    ))}
                    {aiCtqs.stakeholder_needs?.length > 0 && (
                      <div className="space-y-2 border-t border-border/60 pt-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Stakeholder Needs</p>
                        {aiCtqs.stakeholder_needs.map((sn, i) => (
                          <div key={i} className="rounded-lg border border-border bg-card/70 p-2 text-xs">
                            <span className="font-medium">{sn.stakeholder}:</span> {sn.need}{" "}
                            <span className="text-muted-foreground">({sn.success_metric})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {!aiCtqs && !aiAssistStatus?.ctqs?.error && (
                  <ToolStateNotice
                    title="Draft Suggestions"
                    message="Generate a draft to review likely CTQs, measurable requirements, and stakeholder expectations without overwriting the core Problem fields."
                    testId="ai-ctqs-idle"
                  />
                )}
              </AssistanceToolCard>

              <AssistanceToolCard
                title="AI: Scope Guardrails"
                description="Generate in-scope and out-of-scope guidance to tighten boundaries before chartering."
                action={(
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={generateScope}
                    disabled={!!aiGenerating || !problemStatement.problem_text}
                    className="gap-2"
                  >
                    {aiGenerating === "Scope Guardrails" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Suggest Scope
                    <CreditCostTag cost={1} />
                  </Button>
                )}
              >
                {aiAssistStatus?.scope?.error && (
                  <ToolStateNotice
                    tone="error"
                    title="Scope Draft Failed"
                    message={aiAssistStatus.scope.error}
                    testId="ai-scope-error"
                  />
                )}
                {aiScope?.ai_unavailable && (
                  <ToolStateNotice
                    tone="warning"
                    title="Scope Draft Unavailable"
                    message="The AI service did not return scope guardrails this time. Save the latest Problem framing or retry when the service is available."
                    testId="ai-scope-unavailable"
                  />
                )}
                {aiScope && !aiScope.ai_unavailable && (
                  <div className="space-y-3" data-testid="ai-scope-panel">
                    <DefineAiDisclosure
                      title="AI-generated scope draft"
                      detail="Scope guardrails may include assumptions and placeholders inferred from Define context. Confirm boundaries, constraints, and risks with the project sponsor and process owner."
                      testId="ai-scope-disclosure"
                    />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-400">In Scope</p>
                        {aiScope.in_scope?.map((item, i) => (
                          <p key={i} className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-2 text-xs">{item}</p>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-red-400">Out of Scope</p>
                        {aiScope.out_of_scope?.map((item, i) => (
                          <p key={i} className="rounded-lg border border-red-500/20 bg-red-500/10 p-2 text-xs">{item}</p>
                        ))}
                      </div>
                    </div>
                    {aiScope.risks?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400">Scope Creep Risks</p>
                        {aiScope.risks.map((risk, i) => (
                          <p key={i} className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-2 text-xs">{risk}</p>
                        ))}
                      </div>
                    )}
                    {aiScope.assumptions?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300">Assumptions To Validate</p>
                        {aiScope.assumptions.map((item, i) => (
                          <p key={i} className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-2 text-xs">{item}</p>
                        ))}
                      </div>
                    )}
                    {aiScope.constraints?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Constraints</p>
                        {aiScope.constraints.map((item, i) => (
                          <p key={i} className="rounded-lg border border-border bg-card/70 p-2 text-xs">{item}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {!aiScope && !aiAssistStatus?.scope?.error && (
                  <ToolStateNotice
                    title="Boundary Draft"
                    message="Generate a draft to review in-scope boundaries, exclusions, constraints, and scope-creep risks."
                    testId="ai-scope-idle"
                  />
                )}
              </AssistanceToolCard>

              <AssistanceToolCard
                title="AI: Project Charter Draft"
                description="Generate charter language after the problem framing is in reasonable shape."
                action={(
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={generateCharter}
                    disabled={!!aiGenerating || !problemStatement.problem_text}
                    className="gap-2"
                    data-testid="ai-generate-charter-btn"
                  >
                    {aiGenerating === "Project Charter" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Generate Charter
                    <CreditCostTag cost={15} />
                  </Button>
                )}
              >
                {aiAssistStatus?.charter?.error && (
                  <ToolStateNotice
                    tone="error"
                    title="Charter Draft Failed"
                    message={aiAssistStatus.charter.error}
                    testId="ai-charter-error"
                  />
                )}
                {aiCharter?.ai_unavailable && (
                  <ToolStateNotice
                    tone="warning"
                    title="Charter Draft Unavailable"
                    message="The AI service did not return a charter draft. Retry when service capacity is available or after completing more Define inputs."
                    testId="ai-charter-unavailable"
                  />
                )}
                {aiCharter && !aiCharter.ai_unavailable && (
                  <div className="space-y-3" data-testid="ai-charter-panel">
                    <DefineAiDisclosure
                      title="AI-generated charter draft"
                      detail="Charter language may include assumed baselines, placeholder milestones, and estimated financial framing. Review before sharing as a formal charter."
                      testId="ai-charter-disclosure"
                    />
                    {aiCharter.charter_summary && (
                      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">Executive Summary</p>
                        <p className="text-sm">{aiCharter.charter_summary}</p>
                      </div>
                    )}
                    {aiCharter.business_case && (
                      <div className="rounded-lg border border-border bg-card/70 p-3">
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Business Case</p>
                        <p className="text-sm">{aiCharter.business_case}</p>
                      </div>
                    )}
                    {aiCharter.goal_statement && (
                      <div className="rounded-lg border border-border bg-card/70 p-3">
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Goal Statement</p>
                        <p className="text-sm">{aiCharter.goal_statement}</p>
                      </div>
                    )}
                    {aiCharter.milestones?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">DMAIC Milestones</p>
                        <div className="grid gap-2">
                          {aiCharter.milestones.map((milestone, i) => (
                            <div key={i} className="flex items-center gap-3 rounded-lg border border-border bg-card/70 p-2 text-xs">
                              <span className="w-16 shrink-0 font-medium">{milestone.phase}</span>
                              <span className="flex-1">{milestone.deliverable}</span>
                              <span className="shrink-0 text-muted-foreground">{milestone.target_date}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {aiCharter.financial_summary && (
                      <div className="rounded-lg border border-border bg-card/70 p-3">
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Financial Summary</p>
                        <p className="text-sm">{aiCharter.financial_summary}</p>
                      </div>
                    )}
                  </div>
                )}
                {!aiCharter && !aiAssistStatus?.charter?.error && (
                  <ToolStateNotice
                    title="Charter Draft"
                    message="Generate a draft charter summary, business case, goal framing, and milestone outline from the current Define context."
                    testId="ai-charter-idle"
                  />
                )}
              </AssistanceToolCard>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      <AlertDialog open={confirmOperationalDefinitionOverwriteOpen} onOpenChange={setConfirmOperationalDefinitionOverwriteOpen}>
        <AlertDialogContent data-testid="operational-definition-overwrite-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the current operational definition draft?</AlertDialogTitle>
            <AlertDialogDescription>
              Re-generating will replace the current Operational Definition fields with a new AI draft for this section only.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="rounded-lg border border-border bg-background/40 p-4 text-sm text-muted-foreground">
            Existing manual edits will remain unchanged unless you confirm this replace action.
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-operational-definition-overwrite-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmOperationalDefinitionOverwrite} data-testid="confirm-operational-definition-overwrite-btn">
              Confirm Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
